import { useMemo, useState, type FormEvent } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Notice } from "@/components/crm/blocks";
import { OptionList } from "@/components/crm/OptionList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { useCrm } from "@/hooks/useCrm";
import { fieldOptions, getAction, initialValues, type ActionField, type FormValue, type Matches } from "@/lib/actions";
import { ApiError, errorTitle } from "@/lib/api";
import type { ActionContext, ActionResult } from "@/lib/types";
import { cn } from "@/lib/utils";

// Het actievenster (contract §6.3): het formulier van één actie, uitgevoerd op de server in één
// transactie. Een fout van de server blijft in het venster staan, met de velden die niet kloppen.
// Geeft de actie links terug (formulier- of uploadlinks), dan blijven die zichtbaar om te kopiëren.

export interface ActionRequest {
  actionId: string;
  context: Partial<ActionContext>;
  onDone?: (result: ActionResult) => void;
  /** Waarden die de knop al invult (bv. welk document), boven de standaardwaarden. */
  preset?: Record<string, FormValue>;
  key: number;
}

type Failure = { title: string; message: string; details: { field: string; message: string }[] };

export function ActionDialog({ request, matches, onClose, onSuccess }: { request: ActionRequest; matches: Matches; onClose: () => void; onSuccess: (result: ActionResult) => void }) {
  const crm = useCrm();
  const def = getAction(request.actionId)!;
  const [values, setValues] = useState<Record<string, FormValue>>(() => initialValues(def, crm, request.context, matches, request.preset));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [links, setLinks] = useState<{ label: string; url: string }[] | null>(null);
  const invalid = useMemo(() => new Set(failure?.details.map((d) => d.field)), [failure]);

  const set = (name: string, value: FormValue) => setValues((v) => ({ ...v, [name]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const missing = def.fields.filter((f) => f.required && Array.isArray(values[f.name]) && !(values[f.name] as string[]).length);
    if (missing.length) {
      setFailure({ title: "Kies minstens één optie", message: "", details: missing.map((f) => ({ field: f.name, message: "Kies minstens één optie" })) });
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const result = await crm.runAction(request.actionId, values, request.context);
      onSuccess(result);
      const found = [
        result.extra.formUrl && { label: "Formulierlink", url: result.extra.formUrl },
        result.extra.uploadUrl && { label: "Uploadlink", url: result.extra.uploadUrl },
        ...(result.extra.uploadLinks ?? []).filter((l) => l.url).map((l) => ({ label: l.item, url: l.url }))
      ].filter((l): l is { label: string; url: string } => Boolean(l));
      if (found.length) setLinks(found);
      else onClose();
    } catch (error) {
      setFailure({
        title: errorTitle(error),
        message: error instanceof Error ? error.message : String(error),
        details: error instanceof ApiError ? error.details : []
      });
    } finally {
      setBusy(false);
    }
  };

  const label = (field: string) => def.fields.find((f) => f.name === field)?.label ?? field;

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent aria-describedby="action-purpose">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <div className="text-[11px] font-extrabold uppercase tracking-[.11em] text-blush-ink">{def.module}</div>
            <DialogTitle>{def.label}</DialogTitle>
            <DialogDescription id="action-purpose">{def.purpose}</DialogDescription>
          </DialogHeader>

          <DialogBody>
            {links ? (
              <div className="grid gap-3">
                <Notice tone="blush" title="Uitgevoerd">
                  Kopieer de link{links.length > 1 ? "s" : ""} en stuur ze zelf door: mails vanuit het CRM komen later.
                </Notice>
                {links.map((link) => (
                  <div key={link.url} className="grid gap-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-[.06em] text-navy-700">{link.label}</span>
                    <div className="flex gap-2">
                      <Input readOnly value={link.url} onFocus={(e) => e.target.select()} />
                      <Button size="icon" aria-label="Kopiëren" onClick={() => void navigator.clipboard.writeText(link.url).then(() => toast.success("Link gekopieerd"))}>
                        <Copy />
                      </Button>
                      <Button size="icon" asChild aria-label="Openen">
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {def.effects.length > 0 && (
                  <div className="mb-3.5 rounded-[10px] bg-surface-soft px-3 py-2.5 text-xs">
                    <strong className="text-navy-800">Na bevestiging</strong>
                    <ul className="mt-1 grid gap-0.5 text-muted-foreground">
                      {def.effects.map((effect) => (
                        <li key={effect} className="flex gap-1.5">
                          <Check className="mt-px size-3.5 shrink-0 text-tone-green" /> {effect}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {failure && (
                  <Notice tone="red" title={failure.title} className="mb-3.5">
                    {failure.message}
                    {failure.details.length > 0 && (
                      <ul className="ml-4 mt-1 list-disc">
                        {failure.details.map((d) => (
                          <li key={d.field}>
                            {label(d.field)}: {d.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Notice>
                )}
                {def.fields.length ? (
                  <div className="grid gap-x-3 gap-y-3 sm:grid-cols-2">
                    {def.fields.map((field) => (
                      <ActionInput key={field.name} field={field} value={values[field.name] ?? ""} invalid={invalid.has(field.name)} onChange={(v) => set(field.name, v)} options={() => fieldOptions(def, field, crm, request.context)} />
                    ))}
                  </div>
                ) : (
                  <Notice tone="blush" title="Geen extra invoer nodig">
                    De actie gebruikt het dossier waarin je staat.
                  </Notice>
                )}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            {links ? (
              <Button variant="default" onClick={onClose}>
                Sluiten
              </Button>
            ) : (
              <>
                <Button onClick={onClose} disabled={busy}>
                  Annuleren
                </Button>
                <Button type="submit" variant="default" disabled={busy}>
                  {busy ? "Bezig…" : failure ? "Opnieuw proberen" : "Uitvoeren"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ActionInput({ field, value, invalid, onChange, options }: { field: ActionField; value: FormValue; invalid: boolean; onChange: (value: FormValue) => void; options: () => { value: string; label: string }[] }) {
  const id = `action-${field.name.replace(/[^a-z0-9]/gi, "")}`;
  const wide = field.kind === "textarea" || field.kind === "multi" || field.kind === "records";
  const common = { id, required: field.required, "aria-invalid": invalid || undefined, className: cn(invalid && "border-tone-red") };
  let control;
  if (field.kind === "multi" || field.kind === "records") {
    control = <OptionList options={options()} value={Array.isArray(value) ? value : []} onChange={onChange} invalid={invalid} labelledBy={`${id}-label`} />;
  } else if (field.kind === "select" || field.kind === "record") {
    const list = options();
    control = list.length ? (
      <NativeSelect {...common} value={String(value)} onChange={(e) => onChange(e.target.value)}>
        {!field.required && <option value="">— kies —</option>}
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    ) : (
      <NativeSelect {...common} value="" disabled>
        <option value="">Geen opties beschikbaar</option>
      </NativeSelect>
    );
  } else if (field.kind === "textarea") {
    control = <Textarea {...common} value={String(value)} onChange={(e) => onChange(e.target.value)} />;
  } else {
    const type = { text: "text", email: "email", date: "date", time: "time", number: "number" }[field.kind];
    control = <Input {...common} type={type} step={field.kind === "number" ? "any" : undefined} value={String(value)} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={cn("grid content-start gap-1.5", wide && "sm:col-span-2")}>
      <label id={`${id}-label`} htmlFor={id} className="text-[11px] font-bold uppercase tracking-[.06em] text-navy-700">
        {field.label}
        {field.required && <span className="text-tone-red"> *</span>}
      </label>
      {control}
    </div>
  );
}
